import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
/**
 * Handles validation error messages
 */
export class ValidationErrorService {
  /**
   * @constructor
   */
  constructor(private translocoService: TranslocoService) {}

  /**
   * a huge string to set baseline for maximum input length
   */
  private get hugeString() {
    return 'a'.repeat(100);
  }

  /**
   * Defines a base maximum input length for input fields of this app
   */
  private get maxInputLength() {
    return 255;
  }

  /**
   * Translates errors
   * @param {AbstractControl} abstractControl
   * @param {string} errorType
   * @return {Promise<string>}
   */
  async translateError(
    // NOSONAR
    abstractControl: AbstractControl,
    errorType?: string
  ): Promise<string> {
    const errors = abstractControl.errors;
    let errorMessage = '';
    let moreInfo = {};
    for (const errorName in errors) {
      if (Object.prototype.hasOwnProperty.call(errors, errorName)) {
        const errorObject = errors[errorName];
        if (typeof errorObject === 'string') {
          errorMessage = errorObject;
        } else {
          if (errorName === 'mask') {
            errorMessage = `invalid-${errorType}`;
          } else {
            errorMessage = `invalid-${errorName}`;
          }
        }
        if (errorName === 'minlength' || errorName === 'maxlength') {
          moreInfo = { requiredLength: errorObject.requiredLength };
        }
        break;
      }
    }
    return firstValueFrom(
      this.translocoService.selectTranslate(errorMessage, moreInfo)
    );
  }

  /**
   * Uses abstract control information to decide proper max length of field
   * @param {AbstractControl} abstractControl
   * @return {number}
   */
  getMaximumLength(abstractControl: AbstractControl): number {
    const validators: any[] = (abstractControl as any)._rawValidators;
    if (!validators || typeof validators[Symbol.iterator] !== 'function') {
      return this.maxInputLength;
    }
    for (const validator of validators) {
      const errors = validator(new UntypedFormControl(this.hugeString));
      const maxLength = errors?.maxlength?.requiredLength;
      if (maxLength) {
        return maxLength;
      }
    }
    return this.maxInputLength;
  }

  /**
   * Discovers all errors a abstrac control might possess
   * @param {AbstractControl} abstractControl
   * @return {string[]}
   */
  getValidators(abstractControl: AbstractControl): string[] {
    const validators: any[] = (abstractControl as any)._rawValidators;
    const errors = validators.reduce((prev, curr) => {
      const validatorErrors = curr(new UntypedFormControl(''));
      prev.push(...Object.keys(validatorErrors));
      return prev;
    }, []);

    return errors;
  }
}
